import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { setEditQualificationData } from "@/module/education/stores/educationSlice";
import useQualification from "../api/useQualification";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import SelectController from "@/components/ui/others/SelectController";
import { useEffect, useMemo, useState } from "react";
import { monthOptions } from "@/config/constants";
import DocumentMultyUploadSection, {
  DocumentFile,
} from "@/components/doc/DocumentMultyUpload";
import { useQualificationMutation } from "../api/useQualificationMutation";
import { refetchLeadQuals } from "../api/useQualificationByLead";

const EditQualificationSchema = z.object({
  level: z.string().min(1, "Qualification level is required"),
  universityCollege: z.string().min(1, "Institution name is required"),
  scoreValue: z.string().min(1, "Score is required"),
  qualification: z.string(),
  specs: z.string(),
  yearOfPassout: z.string().min(1, "Year of passout is required"),
  monthOfPassout: z.string().min(1, "Month of passout is required"),
});

type EditQualificationFormData = z.infer<typeof EditQualificationSchema>;

const MONTH_NAMES = [
  "january","february","march","april","may","june",
  "july","august","september","october","november","december",
];

function resolveMonthValue(raw: string | number | undefined): string {
  if (!raw) return "";
  const num = Number(raw);
  if (!isNaN(num) && num >= 1 && num <= 12) return String(num);
  const lower = String(raw).trim().toLowerCase();
  const idx = MONTH_NAMES.findIndex((m) => m.startsWith(lower.slice(0, 3)));
  return idx >= 0 ? String(idx + 1) : "";
}

export default function EditQualificationModal() {
  const dispatch = useAppDispatch();
  const [documents, setDocuments] = useState<DocumentFile[]>([]);

  const { editQualificationData } = useAppSelector(
    (state) => state.educationSlice
  );

  const isOpen = !!editQualificationData;
  const qualData = editQualificationData;

  const { levelSelects, qualiSelects, qualifications } = useQualification();
  const { updateQualificationScore } = useQualificationMutation();

  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    setValue: _setValue,
    formState: { errors },
  } = useForm<EditQualificationFormData>({
    resolver: zodResolver(EditQualificationSchema),
    defaultValues: {
      level: "",
      qualification: "",
      specs: "",
      universityCollege: "",
      scoreValue: "",
      yearOfPassout: "",
      monthOfPassout: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!qualData) {
      setDocuments([]);
      return;
    }
    if (qualData.documents?.length) {
      setDocuments(
        qualData.documents.map((doc: any) => ({
          id: String(doc.id),
          fileName: doc.fileName,
          size: doc.size || 0,
          type: doc.type || doc.fileName?.split(".").pop()?.toUpperCase() || "FILE",
          createdAt: doc.createdAt,
          fileUrl: doc.fileUrl,
        }))
      );
    } else {
      setDocuments([]);
    }
    reset({
      level: qualData.qualificationLevel?.id
        ? String(qualData.qualificationLevel.id)
        : "",
      qualification: qualData.qualification?.id
        ? String(qualData.qualification.id)
        : "",
      specs: qualData.specialization?.id
        ? String(qualData.specialization.id)
        : "",
      universityCollege: qualData.nativeUniversity ?? "",
      scoreValue: qualData.minScore != null ? String(qualData.minScore) : "",
      yearOfPassout: qualData.yearOfPassout ?? "",
      monthOfPassout: resolveMonthValue(qualData.monthOfPassout),
    });
  }, [qualData, reset]);

  const selectedLevelId = watch("level");
  const selectedQualiId = watch("qualification");

  const filteredQualifications = useMemo(() => {
    if (!selectedLevelId) return [];
    return qualiSelects.filter((q: any) => {
      const raw = qualifications.find((rq: any) => String(rq.id) === q.value);
      return String(raw?.qualificationLevelId) === selectedLevelId;
    });
  }, [selectedLevelId, qualiSelects, qualifications]);

  const filteredSpecializations = useMemo(() => {
    if (!selectedQualiId) return [];
    const selectedQualObj = qualifications.find(
      (q: any) => String(q.id) === selectedQualiId
    );
    return (
      selectedQualObj?.specializations?.map((s: any) => ({
        value: String(s.id),
        name: s.name,
      })) || []
    );
  }, [selectedQualiId, qualifications]);

  const handleAddDocuments = (files: FileList) => {
    const newDocs: DocumentFile[] = Array.from(files).map((file) => {
      const ext = file.name.split(".").pop()?.toUpperCase() || "FILE";
      return {
        id: `${Date.now()}-${Math.random()}`,
        file,
        fileName: file.name,
        size: file.size,
        type: ext,
        createdAt: new Date().toISOString(),
      };
    });
    setDocuments((prev) => [...prev, ...newDocs]);
  };

  const handleRemoveDocument = (doc: DocumentFile) => {
    setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
  };

  const handleClose = () => {
    reset();
    setDocuments([]);
    dispatch(setEditQualificationData(null));
  };

  const onSubmit = (data: EditQualificationFormData) => {
    if (!qualData?.id) return;

    updateQualificationScore.mutate(
      {
        id: qualData.id,
        data: {
          level: data.level,
          qualification: data.qualification,
          specs: data.specs,
          universityCollege: data.universityCollege,
          scoreValue: data.scoreValue,
          yearOfPassout: data.yearOfPassout,
          monthOfPassout: data.monthOfPassout,
        },
      },
      {
        onSuccess: () => {
          handleClose();
          refetchLeadQuals();
        },
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto rounded-[24px] p-0 gap-0 border-none shadow-2xl"
        showCloseButton={false}
      >
        <DialogHeader className="px-4 pt-[16px] pb-[11px] border-b border-[#e6e8e7] sticky top-0 bg-white z-20">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col gap-[2px] flex-1">
              <p className="text-[20px] font-medium text-black tracking-[-0.8px]">
                Edit qualification
              </p>
              <p className="text-[14px] text-[#505e59]">
                Update the academic qualification details
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="rounded-full"
            >
              <X className="size-4" strokeWidth={1.5} />
            </Button>
          </div>
        </DialogHeader>

        <div className="bg-[#f8f8f8] px-4 py-4 flex flex-col gap-4">
          <div className="flex justify-center items-start gap-5">
            <SelectController
              className="w-[50%]"
              errors={errors}
              control={control}
              name="level"
              title="Level"
              options={levelSelects}
            />

            <SelectController
              className="w-[50%]"
              errors={errors}
              control={control}
              name="qualification"
              title="Qualification"
              options={filteredQualifications}
              disabled={!selectedLevelId}
            />
          </div>

          <SelectController
            errors={errors}
            control={control}
            name="specs"
            title="Specialization"
            options={filteredSpecializations}
            disabled={!selectedQualiId}
          />

          <div className="flex justify-center items-start gap-5">
            <div className="flex flex-col gap-[6px] w-[50%]">
              <Label htmlFor="universityCollege">University/College</Label>
              <Input
                {...register("universityCollege")}
                id="universityCollege"
                placeholder="Enter institution name"
              />
              {errors.universityCollege && (
                <span className="text-xs text-red-500 font-medium">
                  {errors.universityCollege.message}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-[6px] w-[50%]">
              <Label htmlFor="scoreValue">Score Value</Label>
              <Input
                {...register("scoreValue")}
                id="scoreValue"
                placeholder="Enter percentage/grade"
              />
              {errors.scoreValue && (
                <span className="text-xs text-red-500 font-medium">
                  {errors.scoreValue.message}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-center items-start gap-5">
            <div className="flex flex-col gap-[6px] w-[50%]">
              <Label htmlFor="yearOfPassout">Year of Passout</Label>
              <Input
                {...register("yearOfPassout")}
                id="yearOfPassout"
                placeholder="Enter year (e.g., 2024)"
              />
              {errors.yearOfPassout && (
                <span className="text-xs text-red-500 font-medium">
                  {errors.yearOfPassout.message}
                </span>
              )}
            </div>

            <SelectController
              className="w-[50%] m-h-[50px]"
              errors={errors}
              control={control}
              name="monthOfPassout"
              title="Month of Passout"
              options={monthOptions}
            />
          </div>

          <DocumentMultyUploadSection
            documents={documents}
            onAdd={handleAddDocuments}
            onRemove={handleRemoveDocument}
            maxDocuments={4}
          />
        </div>

        <DialogFooter className="px-4 pb-4 pt-4 border-t border-[#e6e8e7] flex justify-end gap-2 bg-white rounded-b-[24px] sticky bottom-0 z-20">
          <Button
            variant="ghost"
            type="button"
            onClick={handleClose}
            className="w-[120px] text-[#da2a46] hover:bg-red-50 rounded-[12px]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit)}
            variant="primary"
            className="h-[40px] px-6 rounded-[12px] font-medium shadow-sm transition-all active:scale-95"
          >
            Update Qualification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
